import {CategoryManager} from '@/library/category';
import {Component, Prop, Vue, Watch} from 'vue-facing-decorator';
import store from '../../../store';
import {errorHandler} from '@/utils/portuna';
import dateHelper from '../../../utils/portuna/dateHelper';
import {getDateInformation} from '../../date';
import LocalStorage from '../../localStorage';
import {strtotime} from '../../strtotime';
import {NoOperation} from '../../utils';
import {calendarResourceOptions} from './options';

let currentCalendar: Nullable<CalendarData>;
let optionFetchPromise: Nullable<Promise<ServiceCalendarResourceOptions>> = null;

@Component
export class ACalendarComponent extends Vue
{
    @Prop({
        type: String,
        required: true,
    }) resource: CalendarResources;

    @Prop({
        type: Object,
        default: () =>
        {
            return {};
        },
    }) additionalData: AdditionalCalendarData;

    categoryManager: CategoryManager = new CategoryManager();

    calendarHasTileView = false;

    serviceResourceOptions: Nullable<ServiceCalendarResourceOptions> = null;

    zoom: number = 1;

    get hourHeight()
    {
        return store.state.calendarHourHeight;
    }

    get minuteHeight()
    {
        return store.getters.calendarMinuteHeight;
    }

    get halfHourHeight()
    {
        return this.hourHeight / 2;
    }

    get dailyHourHeight()
    {
        return this.hourHeight * 24;
    }

    get routes()
    {
        return {
            list: this.resource + '-list',
            view: this.resource + '-entry-view',
            add: this.resource + '-entry-add',
            edit: this.resource + '-entry-edit',
            copy: this.resource + '-entry-copy',
            request: this.resource + '-entry-request',
        };
    }

    get options(): CalendarResourceOptions | ServiceCalendarResourceOptions
    {
        if (this.resource === 'services')
        {
            return this.serviceResourceOptions || <ServiceCalendarResourceOptions>{};
        }

        return calendarResourceOptions[this.resource];
    }

    // may reset calendar view, if switched between group tile-view & calendar
    get calendarViewType(): string
    {
        const defaultView = this.calendarHasTileView ? 'tile' : 'week';
        const type        = this.$route.params.type || LocalStorage.get(this.resource + '.calendar.lastView', defaultView);
        let views         = ['overview', 'day', 'week', '5days', '7days', 'month', 'table', 'request'];

        if (this.calendarHasTileView)
        {
            views.push('tile');
        }

        return views.includes(type) ? type : defaultView;
    }

    get groupViewType(): string
    {
        const type = this.$route.params.type || LocalStorage.get(this.resource + '.group.lastView', 'tile');

        let availableViews = ['tile', 'day', 'week', '5days', '7days', 'month', 'table', 'request'];

        if (this.resource === 'services')
        {
            availableViews = ['day', 'week', 'tile'];
        }

        return availableViews.includes(type) ? type : 'tile';
    }

    get calendarViewDate(): string
    {
        return this.$route.params.date || LocalStorage.get(this.resource + '.lastDate', dateHelper('Y-m-d'));
    }

    get todayLinkParams()
    {
        return {
            name: this.$route.name as string,
            params: {
                id: this.$route.params.id,
                type: this.$route.params.type || this.getViewType(currentCalendar),
                date: 'today',
            },
        };
    }

    get today()
    {
        return dateHelper('Y-m-d') as string;
    }

    get isToday()
    {
        const currentDateInformation = getDateInformation(this.calendarViewDate);
        const todayDateInformation   = getDateInformation(this.today);
        const viewType               = this.getViewType(currentCalendar);

        // same date
        return todayDateInformation.date === currentDateInformation.date
            // week view + same week
            || ('week' === viewType && todayDateInformation.year === currentDateInformation.year && todayDateInformation.week === currentDateInformation.week)
            // month view + same month
            || ('month' === viewType && todayDateInformation.year === currentDateInformation.year && todayDateInformation.month === currentDateInformation.month);
    }

    get monthDays(): DateInformation[][]
    {
        const start = strtotime('monday this week', dateHelper('U', dateHelper('Y-m-01', this.calendarViewDate)) as number);
        const end   = strtotime('this week sunday', (dateHelper('U', dateHelper('Y-m-t', this.calendarViewDate)) as number));
        let current = start;

        let days  = [];
        let row   = [];
        let index = 0;
        do
        {
            index++;
            row.push(getDateInformation(current));
            current = strtotime('+1 day', current);

            if (index && !(index % 7))
            {
                days.push(row);
                row   = [];
                index = 0;
            }
        }
        while (current <= end);

        return days;
    }

    @Watch('zoom')
    watchZoom(newValue: number)
    {
        store.commit('setCalendarHourHeight', Number(newValue) * 60);
    }

    created()
    {
        this.handleResourceOptions().then(NoOperation);

        this.zoom = store.getters.calendarMinuteHeight;
    }

    changeCalendarEntry(calendar: CalendarData)
    {
        currentCalendar = calendar;
    }

    handleResourceOptions(): Promise<any>
    {
        if (this.resource === 'services')
        {
            if (!optionFetchPromise)
            {
                optionFetchPromise = store.dispatch('module/services/fetchServiceOptions');
            }

            optionFetchPromise
            .then(serviceResourceOptions =>
            {
                this.serviceResourceOptions = Object.assign({}, calendarResourceOptions['services'], serviceResourceOptions);
            });

            return optionFetchPromise;
        }

        return Promise.resolve();
    }

    getViewType(entry: Nullable<CalendarData>)
    {
        let view = 'week';
        if (entry)
        {
            if (entry.group)
            {
                view = this.groupViewType;
            }
            else
            {
                view = this.calendarViewType;

                if (this.resource === 'services')
                {
                    if (!['day', 'week'].includes(view))
                    {
                        view = 'week';
                    }
                }
                else if (entry.all_day_only)
                {
                    if (['day', 'week', '5days', '7days'].includes(view))
                    {
                        view = 'month';
                    }
                }
            }
        }

        // quirk fix for old 'list' view -> now 'table'
        return 'list' === view ? 'table' : view;
    }

    getType(entry: CalendarData | CurrentCalendarData)
    {
        return entry.group ? 'group' :
            (entry.link ? 'link' :
                'calendar');
    }

    getTypeName(entry: CalendarData)
    {
        const names = {
            calendar: this.options.calendarType,
            group: 'Ordner',
            link: 'Verbund',
        };

        return names[this.getType(entry)];
    }

    getTypeIcon(entry: CalendarData)
    {
        const icons = {
            calendar: this.options.calendarIcon,
            group: 'folder',
            link: 'link',
        };

        return icons[this.getType(entry)];
    }

    getTypeIconColor(entry: CalendarData)
    {
        const icons = {
            calendar: this.options.moduleColor,
            group: '#cd7f0f',
            link: null,
        };

        return entry.activated ? icons[this.getType(entry)] : null;
    }

    goToToday()
    {
        this.$router.push(this.todayLinkParams).catch(errorHandler);
    }

    goToDate(date: string)
    {
        if (this.calendarViewDate !== date)
        {
            this.$router.push({
                name: this.$route.name as string,
                params: {
                    id: this.$route.params.id,
                    type: this.$route.params.type || this.getViewType(currentCalendar),
                    date,
                },
            }).catch(errorHandler);
        }
    }

    goToCalendar(calendar: number | string)
    {
        if ('number' === typeof calendar)
        {
            calendar = calendar.toString();
        }

        if (calendar === '' + this.$route.params.id)
        {
            return;
        }

        this.$router.push({
            name: this.$route.name as string,
            params: {
                id: calendar,
                type: this.$route.params.type,
                date: this.$route.params.date || this.calendarViewDate,
            },
        }).catch(errorHandler);
    }

    applySerialChanges(entries: Nullable<CalendarEntryListData[]>, entry: CalendarEntryData | CalendarEntryListData, serial: boolean = false, callback: ({
        entries,
        entry,
        index,
    }: { entries: Nullable<CalendarEntryListData[]>, entry: CalendarEntryListData, index: number }) => void)
    {
        if (entries)
        {
            for (let i = entries.length - 1; i >= 0; i--)
            {
                if (
                    entries[i].id === entry.id
                    && (
                        entries[i].recurrence_start === entry.recurrence_start
                        || serial && entries[i].recurrence_start > entry.recurrence_start
                    )
                )
                {
                    callback({
                        entries,
                        entry: entries[i],
                        index: i,
                    });
                }
            }
        }
    }
}

export default ACalendarComponent;